import { useCallback } from 'react';
import { TrashIcon } from 'assets/icons';
import Button from 'components/button';
import { cardIconButtonClass } from 'components/card/PanelCard';
import { fillText } from 'components/invoiceImport/invoiceImportText';
import Tooltip from 'components/tooltips/Tooltips';
import UseContext from 'hooks/useContext';
import {
  COMMENT_COUNTER_FROM,
  COMMENT_MAX_LENGTH,
  authorInitialsOf,
  authorNameOf,
  commentLengthOf,
  deleteErrorText,
  formatCommentAt,
  isCommentSendable,
  sendErrorText,
} from './invoiceCommentText';

// The confirmation stays in the row: the Admin shows one modal at a time,
// and the panel is already under it.
const DeleteConfirmation = ({ comment, comments, text }) => {
  const isDeleting = comments.deletingIds.includes(comment.id);
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-gray-700">
      <span>{text('deleteConfirm', 'Supprimer ce commentaire ?')}</span>
      <Button
        size="SM"
        color={isDeleting ? 'DISABLED' : 'SECONDARY'}
        disabled={isDeleting}
        onClick={comments.cancelDelete}
      >
        {text('deleteCancel', 'Annuler')}
      </Button>
      <Button
        size="SM"
        color={isDeleting ? 'DISABLED' : 'PRIMARY'}
        disabled={isDeleting}
        aria-busy={isDeleting || undefined}
        onClick={() => comments.confirmDelete(comment.id)}
      >
        {text('deleteSubmit', 'Supprimer')}
      </Button>
    </div>
  );
};

const CommentRow = ({ comment, comments, text }) => {
  // Strictly true: the api decides who may delete, an absent flag hides it.
  const canDelete = comment.canDelete === true;
  const isConfirming = canDelete && comments.confirmingDeleteId === comment.id;
  const deleteFailure = comments.deleteFailures[comment.id];
  const deleteLabel = text('deleteLabel', 'Supprimer le commentaire');
  return (
    <li className="flex gap-3 rounded-lg border border-gray-100 px-3 py-3">
      <div
        aria-hidden
        className="flex flex-shrink-0 items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-xs font-medium text-gray-700"
      >
        {authorInitialsOf(comment.author)}
      </div>
      <div className="flex flex-col flex-1 min-w-0">
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0 text-sm">
            <span className="font-medium text-gray-900">
              {authorNameOf(text, comment.author)}
            </span>
            <span className="ml-2 text-gray-500">
              {formatCommentAt(text, comment.createdAt)}
            </span>
          </div>
          {canDelete && !isConfirming && (
            <Tooltip text={deleteLabel} placement="top">
              <button
                type="button"
                className={cardIconButtonClass}
                aria-label={deleteLabel}
                onClick={() => comments.askDelete(comment.id)}
              >
                <TrashIcon wh={18} color="#374151" />
              </button>
            </Tooltip>
          )}
        </div>
        {/* A text node, never markup: React escapes it, pre-wrap keeps its line breaks. */}
        <p className="mt-1 text-sm text-gray-700 whitespace-pre-wrap break-words">
          {comment.text}
        </p>
        {isConfirming && (
          <DeleteConfirmation
            comment={comment}
            comments={comments}
            text={text}
          />
        )}
        {deleteFailure && (
          <div role="alert" className="mt-2 text-sm text-red-600">
            {deleteErrorText(text, deleteFailure)}
          </div>
        )}
      </div>
    </li>
  );
};

const CommentForm = ({ comments, text }) => {
  const { draft, changeDraft, send, isSending, sendFailure } = comments;
  const length = commentLengthOf(draft);
  const isTooLong = length > COMMENT_MAX_LENGTH;
  const canSend = isCommentSendable(draft) && !isSending;
  return (
    <div className="flex flex-col gap-2">
      <textarea
        aria-label={text('inputLabel', 'Votre commentaire')}
        placeholder={text('placeholder', 'Écrire un commentaire…')}
        value={draft}
        readOnly={isSending}
        rows={3}
        className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:border-yellow-500"
        onChange={(event) => changeDraft(event.target.value)}
      />
      {length >= COMMENT_COUNTER_FROM && (
        <div
          aria-live="polite"
          className={`text-xs text-right ${isTooLong ? 'text-red-600' : 'text-gray-500'}`}
        >
          {fillText(text('counter', '{count} / {max}'), {
            count: length,
            max: COMMENT_MAX_LENGTH,
          })}
          {isTooLong && (
            <span className="ml-2">
              {text('tooLong', 'Un commentaire fait au plus 2 000 caractères.')}
            </span>
          )}
        </div>
      )}
      {sendFailure && (
        <div role="alert" className="text-sm text-red-600">
          {sendErrorText(text, sendFailure)}
        </div>
      )}
      <div className="flex justify-end">
        <Button
          color={canSend ? 'PRIMARY' : 'DISABLED'}
          disabled={!canSend}
          aria-busy={isSending || undefined}
          onClick={send}
        >
          {text('submit', 'Commenter')}
        </Button>
      </div>
    </div>
  );
};

/**
 * The "Commentaires" tab of an invoice's detail (JBWEB-1279): notes the admins
 * write to each other, oldest first. They live in Jobbiz only (api decision
 * 58) and are never edited; their author or a super-admin may delete one
 * (JBWEB-1389), which the history records without its text. `comments`
 * comes from `useInvoiceComments`, held by the panel.
 */
const InvoiceCommentsTab = ({ comments }) => {
  const { language } = UseContext();
  const text = useCallback(
    (key, fallback) => language.getValue(`invoiceComments.${key}`, fallback),
    [language],
  );

  if (comments.failure) {
    return (
      <div
        role="alert"
        className="flex flex-col items-center gap-3 py-16 text-center text-sm text-gray-700"
      >
        <div>
          {comments.failure === 'notFound'
            ? text(
                'notFound',
                'Cette facture est introuvable : elle n’a pas de commentaires.',
              )
            : text('loadFailed', 'Les commentaires n’ont pas pu être chargés.')}
        </div>
        {comments.failure === 'failed' && (
          <Button color="SECONDARY" onClick={comments.reload}>
            {text('retryLoad', 'Réessayer')}
          </Button>
        )}
      </div>
    );
  }
  if (!comments.items) {
    return (
      <div role="status" className="py-16 text-center text-sm text-gray-500">
        {text('loading', 'Chargement des commentaires…')}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {comments.items.length === 0 ? (
        <div className="py-8 text-center text-sm text-gray-500">
          {text('empty', 'Aucun commentaire')}
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {comments.items.map((comment) => (
            <CommentRow
              key={comment.id}
              comment={comment}
              comments={comments}
              text={text}
            />
          ))}
        </ul>
      )}
      <CommentForm comments={comments} text={text} />
    </div>
  );
};

export default InvoiceCommentsTab;
